import { AwsRum, type AwsRumConfig } from "aws-rum-web";
// The attribution entry is the build aws-rum-web already bundles; the plain
// "web-vitals" entry is a separate build and would ship a second copy.
import { onFCP, type FCPMetric } from "web-vitals/attribution";

declare global {
  interface Window {
    GLOBAL_CLOUDWATCH_APP_ID: string;
    GLOBAL_CLOUDWATCH_IDENTITY_POOL_ID: string;
    GLOBAL_CLOUDWATCH_REGION: string;
    GLOBAL_CLOUDWATCH_SAMPLE_RATE: string;
  }
}

// Holds the initialised client so application code can tag RUM events with
// custom session attributes (see addRumSessionAttributes). null when RUM is
// disabled because its config globals are absent — the helper then no-ops.
let awsRum: AwsRum | null = null;

/**
 * Custom RUM event type carrying First Contentful Paint, which aws-rum-web's
 * built-in web-vitals telemetry omits. The name is a query contract — Logs
 * Insights filters on it:
 *
 *   filter event_type = "first_contentful_paint"
 *   | stats pct(event_details.value, 75) as fcp_p75, count(*) as n by metadata.pageId as page
 *   | sort n desc
 *
 * The app monitor only ingests custom events when its custom-events setting is
 * enabled; while it is disabled these events are dropped server-side.
 */
const FCP_EVENT_TYPE = "first_contentful_paint";

const recordFirstContentfulPaint = (metric: FCPMetric): void => {
  try {
    awsRum?.recordEvent(FCP_EVENT_TYPE, { version: "1.0.0", value: metric.value });
  } catch {
    // RUM telemetry must never break the app
  }
};

/**
 * User agents that announce themselves as crawlers, renderers or audit tools.
 * Their sessions are not players, so they are kept out of RUM to stop them
 * skewing the per-operator web-vital percentiles and the alarms built on them.
 * Deliberately separate from isBot in user/ui/utils, which gates product
 * behaviour with a different term list; this one is free to grow on its own.
 * `\bbot\b` is word-bounded so device names that merely contain "bot" (Cubot)
 * still count as real users. Crawlers named `<Name>bot` are listed by name
 * rather than matched by a generic suffix: whatever follows "bot" in a crawler
 * token (slash, space, hyphen, nothing) also follows "CUBOT" in some real
 * phone's model string, so no suffix rule separates the two.
 */
export const RUM_BOT_USER_AGENT_PATTERN =
  /\bbot\b|googlebot|bingbot|yandexbot|ahrefsbot|applebot|twitterbot|petalbot|semrushbot|duckduckbot|linkedinbot|telegrambot|slackbot|adsbot|gptbot|claudebot|amazonbot|bytespider|crawler|spider|HeadlessChrome|Prerender|Chrome-Lighthouse/i;

// A navigator without these properties is treated as a real browser: wrongly
// dropping a player's session costs more than recording one stray bot.
const isAutomatedOrBotSession = (): boolean => {
  if (typeof navigator === "undefined") {
    return false;
  }
  return navigator.webdriver === true || RUM_BOT_USER_AGENT_PATTERN.test(navigator.userAgent ?? "");
};

export const initCloudWatchRum = (): void => {
  const defaultSampleRate = 0.1; // Default to 10% if not specified
  const appId = window.GLOBAL_CLOUDWATCH_APP_ID;
  const identityPoolId = window.GLOBAL_CLOUDWATCH_IDENTITY_POOL_ID;
  const region = window.GLOBAL_CLOUDWATCH_REGION;

  if (!appId || !identityPoolId || !region || isAutomatedOrBotSession()) {
    return;
  }

  const config: AwsRumConfig = {
    sessionSampleRate: window.GLOBAL_CLOUDWATCH_SAMPLE_RATE ? Math.min(1, Math.max(0, parseFloat(window.GLOBAL_CLOUDWATCH_SAMPLE_RATE))) : defaultSampleRate,
    identityPoolId,
    endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
    telemetries: ["performance", "errors", "http"],
    allowCookies: true,
    enableXRay: false,
    signing: true,
  };

  try {
    awsRum = new AwsRum(appId, import.meta.env.npm_package_version ?? "unknown", region, config);
    // Buffered observer: reports FCP even when paint happened before this ran.
    onFCP(recordFirstContentfulPaint);
  } catch {
    // Ignore errors thrown during CloudWatch RUM web client initialization
  }
};

/**
 * Names of the custom RUM session attributes the app emits. Each name is a query
 * contract: CloudWatch Logs Insights filters events on `metadata.<name>`, so the
 * spelling here must match the queries. Add new attributes to this union — that
 * keeps the set typo-proof across call sites and serves as the registry of every
 * attribute we emit. Do not use the reserved `aws:` prefix.
 */
type RumSessionAttributeName = "landingPageCacheHit";

/**
 * Attach custom session attributes to every RUM event recorded from now on in
 * this session. Calls accumulate (the SDK merges), so attributes set at
 * different points in the lifecycle all stick. The attributes land in each
 * event's metadata, which lets us segment automatically-collected metrics (LCP,
 * INP, CLS) by application state in CloudWatch Logs Insights — e.g. comparing LCP
 * p75 for landing-page loads that hit the CMS localStorage cache against those
 * that missed it, to prove the cache moved the metric.
 *
 * Web-vital events finalise late (on page hide / first interaction), so an
 * attribute set during page mount is included on them. No-op when RUM is
 * disabled, and never throws — RUM telemetry must not break the app.
 */
export const addRumSessionAttributes = (attributes: Partial<Record<RumSessionAttributeName, string | number | boolean>>): void => {
  try {
    awsRum?.addSessionAttributes(attributes);
  } catch {
    // Ignore errors thrown while tagging RUM events
  }
};
